import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { Button } from '../ui/button';

const SmartAlertsPanel = ({ reducedMotion = false, onScopeFilter }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ops/alerts`);
      const result = await response.json();
      setAlerts(result);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ops/alerts/ack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: alertId })
      });
      setDismissedAlerts(prev => new Set([...prev, alertId]));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const handleAlertClick = (alert) => {
    if (onScopeFilter && alert.scope_filters) {
      onScopeFilter(alert.scope_filters);
    }
  };

  const getAlertIcon = (level) => {
    switch (level) {
      case 'error':
        return AlertCircle;
      case 'warning':
        return AlertTriangle;
      default:
        return Info;
    }
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'error':
        return { bg: '#7f1d1d', border: '#dc2626', icon: '#ef4444' };
      case 'warning':
        return { bg: '#78350f', border: '#f59e0b', icon: '#fbbf24' };
      default:
        return { bg: '#164e63', border: '#C9A24A', icon: '#22d3ee' };
    }
  };

  const visibleAlerts = alerts.filter(a => !dismissedAlerts.has(a.id));

  return (
    <div className="fixed bottom-6 right-6 z-50 space-y-3 max-w-md">
      <AnimatePresence>
        {visibleAlerts.map((alert, index) => {
          const Icon = getAlertIcon(alert.level);
          const colors = getAlertColor(alert.level);

          return (
            <motion.div
              key={alert.id}
              initial={reducedMotion ? {} : { x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={reducedMotion ? {} : { x: 400, opacity: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="backdrop-blur-xl rounded-lg border shadow-2xl overflow-hidden cursor-pointer"
              style={{
                backgroundColor: colors.bg + 'E6',
                borderColor: colors.border
              }}
              onClick={() => handleAlertClick(alert)}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.icon }} />
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-white mb-1">
                      {alert.title}
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {alert.message}
                    </p>
                    {alert.scope_filters && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(alert.scope_filters).map(([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white"
                          >
                            {key}: {value}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 mt-2">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-shrink-0 h-6 w-6 p-0 hover:bg-white/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(alert.id);
                    }}
                  >
                    <X className="w-4 h-4 text-slate-300" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {visibleAlerts.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="backdrop-blur-xl bg-slate-900/80 rounded-lg border border-slate-700 p-4 text-center"
        >
          <p className="text-sm text-slate-400">No active alerts</p>
        </motion.div>
      )}
    </div>
  );
};

export default SmartAlertsPanel;
